import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { ClipLoader } from "react-spinners";

type DeleteModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  onOk: () => Promise<void>;
  itemName: string;
  warningMessage?: string;
};

export const DeleteModal = ({
  isOpen,
  handleClose,
  onOk,
  itemName,
  warningMessage,
}: DeleteModalProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Container>
        <Header>
          <Typography variant="h5">{`Confirm deletion "${itemName}" ?`}</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Header>
        <Content>
          <Typography variant="subtitle1">
            Are you sure you want to delete "{itemName}" ?
          </Typography>
          {warningMessage && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 152, 0, 0.2)"
                    : "rgba(255, 152, 0, 0.1)",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "warning.main",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.text.primary
                      : theme.palette.text.primary,
                  fontWeight: 600,
                  lineHeight: 1.6,
                }}
              >
                ⚠️ {warningMessage}
              </Typography>
            </Box>
          )}
          <Typography variant="subtitle1" sx={{ mt: warningMessage ? 1 : 0 }}>
            This action cannot be undone.
          </Typography>
        </Content>
        <Footer>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={"error"}
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              await onOk();
              setLoading(false);
            }}
            startIcon={
              loading && (
                <ClipLoader color={theme.palette.error.main} size={18} />
              )
            }
            sx={{
              backgroundColor: theme.palette.error.light,
              border: `1px solid ${theme.palette.error.dark}`,
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.error.hoverBg,
                border: `1px solid ${theme.palette.error.hoverBorder}`,
              },
            }}
          >
            Delete
          </Button>
        </Footer>
      </Container>
    </Dialog>
  );
};

const Footer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: "16px",
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: "8px",
}));

const Content = styled("div")(({ theme }) => ({
  padding: "16px",
  backgroundColor: theme.palette.background.default,
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 16px",
  height: "64px",
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Container = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  width: "100%",
  minWidth: "380px",
}));
